<script lang="ts" module>
  /**
   * Der Zeichensatz der Web-App.
   *
   * Vorher standen hier Unicode-Zeichen: ↑ ↓ ✕ ⧉ ↺ ↻ ‹ ● →. Sie sind je nach
   * Schriftart unterschiedlich breit, unterschiedlich schwer und teils gar
   * nicht vorhanden — auf demselben Bildschirm wirkte „✕" fett und „↺" dünn.
   * Dieser Satz ist durchgehend 24×24, Kontur 1,5, runde Enden und
   * `currentColor`, gezeichnet passend zum Gewicht der SF Symbols in der
   * iOS-App.
   *
   * Die Pfade stehen **hier** und nicht in einem Sprite unter `public/`:
   * Inline spart die Anfrage, umgeht jede Frage nach der Content Security
   * Policy und hält den Satz an einer Stelle. Ein zweites Vorkommen liefe
   * über kurz oder lang auseinander.
   *
   * Ein Symbol ist **nie** die Beschriftung. Steht es allein, ist `titel`
   * Pflicht — daraus werden `<title>` und `aria-label`. Ohne Titel gilt es
   * als schmückend und wird für Vorlesegeräte ausgeblendet.
   */
  export const PFADE = {
    'pfeil-hoch': 'M12 19V5M6 11l6-6 6 6',
    'pfeil-runter': 'M12 5v14M18 13l-6 6-6-6',
    'pfeil-rechts': 'M5 12h14M13 6l6 6-6 6',
    'pfeil-runter-fahrt': 'M12 4v16M8.5 16.5 12 20l3.5-3.5',
    zurueck: 'M15 5l-7 7 7 7',
    weiter: 'M9 5l7 7-7 7',
    aufklappen: 'M9 5l7 7-7 7',
    zuklappen: 'M5 9l7 7 7-7',
    schliessen: 'M6 6l12 12M18 6L6 18',
    haken: 'M4 12.5l5.5 5.5L20 6.5',
    frage: 'M9 8.5a3 3 0 1 1 3.6 2.94c-.9.2-1.6 1-1.6 1.96V14M12 17.5v.5',
    plus: 'M12 5v14M5 12h14',
    minus: 'M5 12h14',
    duplizieren: 'M9 9h10v10H9zM5 15V5h10',
    rueckgaengig: 'M4 9h9a5 5 0 0 1 0 10H8M4 9l4-4M4 9l4 4',
    wiederherstellen: 'M20 9h-9a5 5 0 0 0 0 10h5M20 9l-4-4M20 9l-4 4',
    suchen: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4',
    mehr: 'M6 12h.01M12 12h.01M18 12h.01',
    aktualisieren:
      'M20 6v5h-5M4 18v-5h5M19.4 13a7.5 7.5 0 0 1-13.2 3M4.6 11a7.5 7.5 0 0 1 13.2-3',
    karte: 'M9 4 3 7v13l6-3 6 3 6-3V4l-6 3zM9 4v13M15 7v13',
    /* Das Gegenstück zu `karte` — die beiden schalten auf der Karte zwischen
       gezeichnetem Bild und Luftbild um und stehen deshalb nie zugleich da,
       sondern immer an derselben Stelle abwechselnd. Genau darum müssen sie
       sich bei 14 px in der **Silhouette** unterscheiden und nicht im Detail:
       Wer nur die Innenzeichnung ändert, baut einen Knopf, dessen Zustand man
       nicht sieht. `karte` ist ein gefaltetes Vieleck mit Ecken, `globus` ein
       Kreis — das erkennt man auch aus dem Augenwinkel.

       Ein Globus und kein Satellit, obwohl der Modus so heißt: Ein Satellit
       braucht Rumpf und zwei Flügel, also drei Formen in einem 24er-Feld, und
       bei 14 px war das im Entwurf ein Fleck — derselbe Fehler wie beim ersten
       Versuch der gemischten Übernachtung. Der Globus zeigt außerdem das
       Richtige: nicht das Gerät, sondern die Erde, wie sie wirklich aussieht.
       Die iOS-App nimmt dafür SF `globe`, das Zeichen ist also auf beiden
       Plattformen dasselbe.

       Der Meridian ist eine Linse aus zwei Kurven und keine Ellipse: Eine
       durchgezogene senkrechte Linie hätte den Kreis geteilt und nach `halbmond`
       ausgesehen, und der steht im selben Satz. */
    globus:
      'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 1 0 0-17M3.5 12h17M12 3.5c2.9 2.2 4.4 5.1 4.4 8.5s-1.5 6.3-4.4 8.5c-2.9-2.2-4.4-5.1-4.4-8.5s1.5-6.3 4.4-8.5',
    buch: 'M12 6.8C10.6 5.3 8.6 4.6 6 4.6a1 1 0 0 0-1 1v11.6a1 1 0 0 0 1 1c2.6 0 4.6.7 6 2.2 1.4-1.5 3.4-2.2 6-2.2a1 1 0 0 0 1-1V5.6a1 1 0 0 0-1-1c-2.6 0-4.6.7-6 2.2zM12 6.8v13.6',
    auto: 'M3.5 16.5v-3.1l2.2-4.9A2 2 0 0 1 7.5 7.3h9a2 2 0 0 1 1.8 1.2l2.2 4.9v3.1M3.5 13.4h17M3.5 16.5h17M6 16.5v2.2M18 16.5v2.2M7 14.6h.01M17 14.6h.01',
    telefon:
      'M5 4h3.5l1.5 4-2 1.5a11 11 0 0 0 5.5 5.5L15 13l4 1.5V18a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 3 6.2 2 2 0 0 1 5 4z',
    mond: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z',
    /* Die gemischte Übernachtung — halb im Fahrzeug, halb in der Unterkunft.
       Der erste Versuch waren `mond` und `karte` auf halber Größe
       nebeneinander: im Entwurf einleuchtend, im Browser unbrauchbar. Zwei
       Formen in einem 24er-Feld sind bei 14 px in der Reiseliste je sieben
       Pixel breit; sie berührten einander und wurden zu einem Gekritzel, das
       weder nach Mond noch nach Karte aussah. Ein Zeichen, das man erst auf
       96 px erkennt, ist an der Stelle, an der es steht, keines.
       Der Halbmond löst dieselbe Aufgabe mit einer einzigen Form: Er bleibt
       in der Nachtachse, ist gegen die Sichel nicht zu verwechseln und sagt
       „halb und halb" — genau das, was gemischt bedeutet. Die Trennlinie
       muss die Hälften tragen, weil dieser Satz nur Konturen kennt und keine
       Flächen. */
    halbmond: 'M12 4a8 8 0 1 0 0 16 8 8 0 1 0 0-16M12 4v16',
    'kein-netz':
      'M3 3l18 18M5 10.5a13 13 0 0 1 4-2.4M19 10.5a13 13 0 0 0-6.5-2.9M8 14a8 8 0 0 1 2-1.2M16 14a8 8 0 0 0-1.6-1M12 18.5h.01',
    blitz: 'M13 3 5.5 13.5H11l-.8 7.5L18.5 10H13z',
    /* Die fünf Verkehrsmittel eines Abstechers — Portofino per Bus, die
       Cinque Terre per Bahn. Sie stehen dort, wo sonst die Stationsnummer
       steht: in der Stationsliste, am Kartenpin, auf der geteilten Seite. Ein
       Abstecher zählt in der Reihenfolge der Fahrt nicht mit, hat also keine
       Nummer — und ein leerer Kreis an ihrer Stelle sagte nichts.

       Die Silhouetten müssen sich bei 13 px unterscheiden, nicht bei 96. Der
       erste Entwurf hatte Bus und Bahn beide als Kasten mit Fensterband — auf
       dem Bildschirm waren sie in der Liste nicht auseinanderzuhalten, und
       genau das ist die eine Aufgabe dieser fünf Zeichen. Jetzt trägt jedes
       eine eigene Grundform: Kasten auf zwei Rädern, hoher Kasten mit Beinen,
       Dreieck über Rumpf, Strichfigur, zwei Kreise. */
    bus: 'M5.5 5h13a2 2 0 0 1 2 2v7H3.5V7a2 2 0 0 1 2-2zM3.5 9.5h17M7 18a2 2 0 1 1 0-4 2 2 0 0 1 0 4M17 18a2 2 0 1 1 0-4 2 2 0 0 1 0 4',
    bahn: 'M8 4h8a3 3 0 0 1 3 3v8.5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a3 3 0 0 1 3-3zM5 10.5h14M9.2 14h.01M14.8 14h.01M9.5 18 7 21M14.5 18 17 21',
    /* Ein Segelboot und kein Fährschiff: Der Rumpf allein sähe aus wie eine
       Schale, und ein Aufbau mit Fenstern war bei 13 px ein Fleck. Das Dreieck
       über der Wasserlinie ist die Form, die auch klein noch „Boot" sagt. */
    boot: 'M12 3.5v13.5M12 5.5 17.5 14.5H12M4 17h16l-2.2 3.4H6.2z',
    /* Die Gehende: Kopf, Rumpf, zwei Beine, ein Arm. Der zweite Arm fiel weg —
       bei 18 px lief er mit dem Rumpf zusammen und machte einen Klecks. */
    'zu-fuss': 'M12.9 4.6h.01M10.9 20.6l1.4-5.6-2.4-2.3.8-4.3 3.2 1.5 1.8 2.6M10.7 9.4 8 11.6M13.9 15l2.2 5.6',
    rad: 'M6.5 18.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7M17.5 18.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7M6.5 15h5l3.2-5.5M11.5 15l3.2-5.5M14.7 9.5H17l.5 5.5M13.5 8h3',
    warnung: 'M12 4 2.8 20h18.4zM12 10v4.5M12 17.5v.5',
    drucken:
      'M7 9V3.5h10V9M7 18H5a1 1 0 0 1-1-1v-5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5a1 1 0 0 1-1 1h-2M7 14.5h10v6H7z',

    /* ── Erweiterung für die Werkbank ────────────────────────────────────
       Achtzehn Zeichen, die mit dem Stationsblatt und der Reiterleiste
       dazukommen: Jeder Abschnitt des Blattes braucht eines, und jeder Reiter
       eines. Ergänzt, nicht ersetzt — dieselbe Größe, dieselbe Strichstärke,
       dieselben runden Enden wie oben.

       Jedes sagt die Sache, nicht die Handlung: „bett" und nicht „schlafen
       hinzufügen". Wer hier zeichnet, prüft zwei Dinge: Bleibt es bei 18 px
       lesbar, und ist es dasselbe Bild wie auf der Karte für dieselbe Sache? */

    /* Abschnitte des Stationsblatts. */
    bett: 'M3 18v-6h13a4 4 0 0 1 4 4v2M3 18v-7.5M3 12V8M7.5 12V9.5h5V12',
    beleg: 'M6 3.5h12v17l-3-1.6-3 1.6-3-1.6-3 1.6zM9 8h6M9 11.5h6M9 15h3',
    ausweich: 'M12 3.5v9M12 12.5l-5 4.5M12 12.5l5 4.5M5.5 20.5h.01M18.5 20.5h.01',
    /* Das Ausweichziel: ein Wegweiser mit zwei Schildern in zwei Richtungen.

       Bewusst **nicht** `ausweich` (die Gabel), obwohl beide „Plan B" heißen:
       Die Gabel gehört den Ausweichquartieren, und beide Abschnitte stehen an
       einer Übernachtungsstation untereinander. Zwei gleiche Bilder für zwei
       verschiedene Sachen wäre genau der Fehler, den die Zeichenregel dieses
       Satzes verbietet.

       Der Unterschied ist die Aussage: Die Gabel zeigt eine Verzweigung, der
       Wegweiser nennt ein **anderes Ziel**. Zwei Arme und nicht drei — bei
       18 px läuft das dritte Schild mit dem Pfosten zusammen. */
    ausweichziel: 'M12 3.5v17M12 6.5h5.5l2 2-2 2H12M12 12.5H6.5l-2 2 2 2H12',
    tagesplan: 'M4.5 6.5h15v14h-15zM8 3.5v4M16 3.5v4M4.5 11h15M8.5 14.5h3M8.5 17.5h6',
    fussweg: 'M13.5 4.2a1.6 1.6 0 1 0 0-.1M13.2 6.8l-2 3.6 2.8 1.9.9 3.7M11.2 10.4 8.5 12l.9 2.8M15.9 16l-.8 3.2',
    anreise:
      'M4 17.5h4M4 17.5a3 3 0 0 1 3-3h2.5l3-5H16a4 4 0 0 1 4 4v4h-3M11 17.5h3M9.5 17.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM18.5 17.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z',
    kajak:
      'M3.5 12h17M3.5 12c0 3 3.8 5.5 8.5 5.5s8.5-2.5 8.5-5.5M3.5 12c0-3 3.8-5.5 8.5-5.5s8.5 2.5 8.5 5.5M7 5l3.5 3.5M17 19l-3.5-3.5',
    aufenthalt: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17zM12 7.5V12l3.5 2',
    /* Die Tagesgrenzen „ab hier": eine Uhr mit einem Riegel links davor.

       Die Uhr allein wäre `aufenthalt` in klein — und die beiden stehen im
       selben Blatt untereinander. Der senkrechte Strich ist der Unterschied
       und zugleich die Aussage: Er markiert die Stelle, ab der die neue Regel
       gilt, so wie die Kapitelmarke „ab hier beginnt Ligurien". Deshalb ist
       die Uhr kleiner als bei `aufenthalt` (r 6,5 statt 8,5) — sie muss neben
       dem Riegel Platz haben, ohne aus dem 24er-Feld zu laufen. */
    tagesgrenzen: 'M3.5 5.5v13M14 18.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13M14 8.5V12l2.8 1.6',
    notiz: 'M6 3.5h8l4 4v13H6zM14 3.5v4h4M9 12h6M9 15.5h4',
    ort: 'M12 21c4-4.4 6-7.6 6-10.2A6 6 0 0 0 6 10.8C6 13.4 8 16.6 12 21zM12 13a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4z',
    technik: 'M9.5 4h5v2.5h3.5v3h-12v-3h3.5zM4 12.5h16M6.5 12.5v8M17.5 12.5v8M10.5 16h3',

    /* Reiter der Werkbank. `laden` ist die Ladesäule mit Stecker und nicht der
       Blitz: Der Blitz steht schon für den Antrieb Elektro, und ein Reiter ist
       ein Ort, keine Eigenschaft. */
    laden: 'M7 3.5h10M8.5 3.5v4a3.5 3.5 0 0 0 7 0v-4M12 11v4M9.5 15h5v5.5h-5z',
    euro: 'M17 6.5A7 7 0 1 0 17 17.5M4.5 10.5h8M4.5 13.5h8',
    koffer: 'M4 8.5h16v11H4zM9 8.5V5.5h6v3M4 13.5h16',
    teilen:
      'M12 15.5V4M8.5 7.5 12 4l3.5 3.5M5 13.5v5.5a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5v-5.5',
    entdecken: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM15.5 8.5 13.5 13.5 8.5 15.5 10.5 10.5z',
    stationen: 'M6 5.5a2 2 0 1 0 0-.1M6 6v4.5a3 3 0 0 0 3 3h6a3 3 0 0 1 3 3V20M18 20a2 2 0 1 0 0 .1',
    /* Das Cloverleaf der Befehlssuche — dasselbe Zeichen, das auf der Tastatur
       neben der Taste steht, die sie öffnet. */
    befehl: 'M9 9V7a2 2 0 1 0-2 2h10a2 2 0 1 0-2-2v10a2 2 0 1 0 2-2H7a2 2 0 1 0 2 2z',

    /* ── Die Seitenleiste (Navigation A, 14.9.2026) ──────────────────
       Drei Zeichen, die es vorher nicht gab, weil es die Leiste nicht gab.

       `menue` sind drei Striche und keine zwei und keine Punkte: Es ist das
       einzige Bild, das auf jedem Telefon dasselbe bedeutet, und ein Menü ist
       die Stelle, an der man nicht raten will. Es steht nur unter 820 px.

       `konto` ist dieselbe Person wie im Kopf der Kontoseite — Kreis und
       Schulterbogen, kein Zahnrad: Ein Zahnrad hieße „Einstellungen", und die
       Kontoseite ist mehr als das (Freigaben, Speicher, Abmelden).

       `funke` für „Was ist neu": ein Stern mit eingezogenen Flanken, wie ihn
       die Leinwand zeichnet. Kein Glockenzeichen — eine Glocke gibt es in der
       App schon, und dort heißt sie „jemand hat etwas geschrieben". Zwei
       Bedeutungen für ein Bild wären die teuerste Sorte Verwechslung. */
    menue: 'M4 7h16M4 12h16M4 17h16',
    konto: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8M4 21c0-4 3.6-7 8-7s8 3 8 7',
    funke: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z',

    /* Das Titelbild der Reise: Rahmen, Bergkante, Sonne.
       Die Bergkante läuft **bis an beide Ränder** und nicht frei im Rahmen —
       bei 18 px ist ein schwebendes Dreieck ein Fleck, eine durchgehende Kante
       liest sich als Landschaft. Die Sonne ist ein Punkt und kein Kreis: Ein
       Kreis mit 1,5 Kontur bei dieser Größe wäre ein zweiter Fleck. */
    bild: 'M4 5.5h16v13H4zM4 15.5l4.5-4.5 4 4 3-3 4.5 4.5M8.6 9.3h.01'
  } as const;

  export type ZeichenName = keyof typeof PFADE;
</script>

<script lang="ts">
  interface Props {
    name: ZeichenName;
    /** Kantenlänge in px. 18 in Listen, 22 in der Kopfzeile, 15–16 neben Text. */
    groesse?: number;
    strichstaerke?: number;
    /** Wird zu `<title>` und `aria-label`. Pflicht, wenn das Symbol allein steht. */
    titel?: string | null;
  }
  let { name, groesse = 18, strichstaerke = 1.5, titel = null }: Props = $props();
</script>

<svg
  viewBox="0 0 24 24"
  width={groesse}
  height={groesse}
  fill="none"
  stroke="currentColor"
  stroke-width={strichstaerke}
  stroke-linecap="round"
  stroke-linejoin="round"
  role={titel ? 'img' : 'presentation'}
  aria-label={titel ?? undefined}
  aria-hidden={titel ? undefined : 'true'}
>
  {#if titel}<title>{titel}</title>{/if}
  <path d={PFADE[name]} />
</svg>

<style>
  svg { display: block; flex: none; }
</style>
